/**
 * The Items to review tab's state (TMMAPP-0159 §F 27): the count and the tab to show. The strip and
 * the panel are `ReviewTabs.tsx`.
 */
import { useCallback, useState } from 'react';

/** The review tab's value in every page's `?tab=`. */
export const REVIEW_TAB = 'review';
export type ReviewTabValue = typeof REVIEW_TAB;

/** A review count: a number once served, `loading` before, `unavailable` when its read failed. */
export type ReviewCount = number | 'loading' | 'unavailable';

/**
 * The page's count, fed by the review content's reports. A reload in place (`loading` after a served
 * count) keeps the last count, so the label never blinks.
 */
export function useReviewCount(): [ReviewCount, (next: ReviewCount) => void] {
  const [count, setCount] = useState<ReviewCount>('loading');
  const report = useCallback((next: ReviewCount) => {
    setCount((prev) => (next === 'loading' && prev !== 'loading' ? prev : next));
  }, []);
  return [count, report];
}

/**
 * The tab to show: the requested one; with none requested, the default by count, picked once; `null`
 * while that count loads. With `enabled` false the page has nothing to review here (no review tab), so
 * a request for it, or none, is the main tab.
 */
export function useReviewTab<T extends string>(
  requested: T | ReviewTabValue | null,
  count: ReviewCount,
  main: T,
  enabled = true,
): T | ReviewTabValue | null {
  const [initial, setInitial] = useState<T | ReviewTabValue | null>(null);
  if (!enabled) return requested === null || requested === REVIEW_TAB ? main : requested;
  if (requested !== null) {
    // An arrival with a request (a `?tab=`, or a link into the main tab's own state) is kept: clearing
    // that state later never moves the person to Items to review (TMMAPP-0160 item 1).
    if (initial === null) setInitial(requested);
    return requested;
  }
  if (initial !== null) return initial;
  if (count === 'loading') return null;
  const picked: T | ReviewTabValue = typeof count === 'number' && count > 0 ? REVIEW_TAB : main;
  // Picked once and kept (adjusting state while rendering): a later count never moves the person.
  setInitial(picked);
  return picked;
}
